import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  X, 
  Wand2, 
  RefreshCw, 
  AlertCircle, 
  CheckCircle,
  Image as ImageIcon,
  Settings,
  Eye,
  EyeOff
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useFetchStockData, ProductoConImagen, CategoriaData } from '../hooks/useFetchStockData';
import CategoriaFolder from './CategoriaFolder';
import ProductoItem from './ProductoItem';

interface PublicarConIAProps {
  isOpen: boolean;
  onClose: () => void;
  onProductSelect?: (producto: ProductoConImagen) => void;
  selectedProduct?: ProductoConImagen | null;
  title?: string;
  subtitle?: string;
  showImages?: boolean;
  showAllFields?: boolean;
  onGenerateWithAI?: (producto: ProductoConImagen) => void;
  onPublish?: (producto: ProductoConImagen) => void;
}

const PublicarConIA: React.FC<PublicarConIAProps> = ({
  isOpen,
  onClose,
  onProductSelect,
  selectedProduct,
  title = "Publicar con IA",
  subtitle = "Selecciona un producto para publicar con inteligencia artificial",
  showImages = true,
  showAllFields = true,
  onGenerateWithAI,
  onPublish
}) => {
  const { user } = useAuth();
  const {
    categorias,
    loading,
    error,
    updateOnModalOpen,
    getProductsByCategory,
    getAllCategories,
    isCategoryEmpty
  } = useFetchStockData();

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [showEmptyCategories, setShowEmptyCategories] = useState(true);
  const [viewMode, setViewMode] = useState<'folders' | 'list'>('folders');
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [internalShowImages, setInternalShowImages] = useState(showImages);
  const [internalShowAllFields, setInternalShowAllFields] = useState(showAllFields);
  const [showDebugInfo, setShowDebugInfo] = useState(false);

  // Actualizar datos cada vez que se abre el modal
  useEffect(() => {
    if (isOpen && user) {
      updateOnModalOpen();
    }
  }, [isOpen, user, updateOnModalOpen]);

  // Filtrar categorías según búsqueda y configuración
  const filteredCategorias = categorias.filter(categoria => {
    // Filtrar por término de búsqueda
    if (searchTerm && !categoria.name.toLowerCase().includes(searchTerm.toLowerCase())) {
      return false;
    }

    // Filtrar categorías vacías si está deshabilitado
    if (!showEmptyCategories && categoria.isEmpty) {
      return false;
    }

    return true;
  });

  // Obtener productos de una categoría específica
  const getCategoryProducts = (categoryName: string): ProductoConImagen[] => {
    return getProductsByCategory(categoryName);
  };

  // Manejar selección de producto
  const handleProductSelect = (producto: ProductoConImagen) => {
    if (onProductSelect) {
      onProductSelect(producto);
    }
  };

  // Manejar actualización de categoría
  const handleCategoryRefresh = async (categoryName: string) => {
    setIsRefreshing(true);
    try {
      // Forzar actualización de datos
      await updateOnModalOpen();
    } finally {
      setIsRefreshing(false);
    }
  };

  // Manejar generación con IA
  const handleGenerateWithAI = (producto: ProductoConImagen) => {
    if (onGenerateWithAI) {
      onGenerateWithAI(producto);
    }
  };

  // Manejar publicación
  const handlePublish = (producto: ProductoConImagen) => {
    if (onPublish) {
      onPublish(producto);
    }
  };

  // Vista de lista de productos
  const renderListView = () => {
    const allProducts = categorias.flatMap(categoria => 
      categoria.productos.map(producto => ({
        ...producto,
        categoria: categoria.name
      }))
    );

    const filteredProducts = allProducts.filter(producto => {
      if (searchTerm) {
        return (
          producto.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
          producto.marca.toLowerCase().includes(searchTerm.toLowerCase()) ||
          producto.genero.toLowerCase().includes(searchTerm.toLowerCase()) ||
          producto.temporada.toLowerCase().includes(searchTerm.toLowerCase())
        );
      }
      return true;
    });

    return (
      <div className="space-y-3">
        <div className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
          Todos los productos ({filteredProducts.length})
        </div>
        {filteredProducts.map((producto) => (
          <div key={`${producto.categoria}-${producto.sku}`} className="relative">
            <ProductoItem
              producto={producto}
              onSelect={handleProductSelect}
              selected={selectedProduct?.sku === producto.sku}
              showImage={internalShowImages}
              showAllFields={internalShowAllFields}
            />
            <div className="absolute top-2 right-2 text-xs bg-gray-100 dark:bg-slate-700 text-gray-600 dark:text-gray-300 px-2 py-1 rounded">
              {producto.categoria}
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Vista de carpetas por categoría
  const renderFoldersView = () => {
    return (
      <div className="space-y-4">
        {filteredCategorias.map((categoria) => (
          <CategoriaFolder
            key={categoria.id}
            categoria={categoria}
            onProductSelect={handleProductSelect}
            selectedProduct={selectedProduct}
            showImages={internalShowImages}
            showAllFields={internalShowAllFields}
            onRefresh={handleCategoryRefresh}
            loading={isRefreshing}
          />
        ))}
      </div>
    );
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-slate-900 rounded-3xl shadow-2xl max-w-6xl w-full max-h-[90vh] overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="p-6 border-b border-gray-200 dark:border-slate-700 flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-purple-100 dark:bg-purple-900/30 rounded-xl">
                  <Wand2 className="w-6 h-6 text-purple-600" />
                </div>
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 dark:text-gray-100">
                    {title}
                  </h2>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {subtitle}
                  </p>
                </div>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
              >
                <X size={24} />
              </button>
            </div>

            {/* Contenido */}
            <div className="p-6 max-h-[70vh] overflow-y-auto">
              {/* Controles */}
              <div className="mb-6 space-y-4">
                {/* Barra de búsqueda */}
                <div className="flex items-center space-x-4">
                  <div className="flex-1">
                    <input
                      type="text"
                      placeholder="Buscar por categoría, SKU, marca, género o temporada..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                    />
                  </div>
                  <button
                    onClick={() => updateOnModalOpen()}
                    disabled={loading || isRefreshing}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200 disabled:opacity-50 disabled:cursor-not-allowed"
                    title="Actualizar datos"
                  >
                    <RefreshCw className={`w-5 h-5 ${loading || isRefreshing ? 'animate-spin' : ''}`} />
                  </button>
                  
                  <button
                    onClick={() => setShowDebugInfo(!showDebugInfo)}
                    className="p-2 rounded-lg text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 transition-colors duration-200"
                    title="Mostrar información de debug"
                  >
                    <Settings className="w-5 h-5" />
                  </button>
                </div>

                {/* Controles de vista */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-4">
                    {/* Toggle de categorías vacías */}
                    <label className="flex items-center space-x-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={showEmptyCategories}
                        onChange={(e) => setShowEmptyCategories(e.target.checked)}
                        className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <span className="text-sm text-gray-700 dark:text-gray-300">
                        Mostrar categorías vacías
                      </span>
                    </label>
                  </div>

                  {/* Modo de vista */}
                  <div className="flex items-center space-x-2">
                    <button
                      onClick={() => setViewMode('folders')}
                      className={`p-2 rounded-lg transition-colors duration-200 ${
                        viewMode === 'folders'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title="Vista de carpetas"
                    >
                      <ImageIcon className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setViewMode('list')}
                      className={`p-2 rounded-lg transition-colors duration-200 ${
                        viewMode === 'list'
                          ? 'bg-primary-100 dark:bg-primary-900/30 text-primary-600'
                          : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700'
                      }`}
                      title="Vista de lista"
                    >
                      <Settings className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>

              {/* Contenido principal */}
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600"></div>
                  <span className="ml-3 text-gray-600 dark:text-gray-300">
                    Cargando productos...
                  </span>
                </div>
              ) : error ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
                    <AlertCircle className="w-8 h-8 text-red-600" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    Error cargando datos
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    {error}
                  </p>
                  <button
                    onClick={() => updateOnModalOpen()}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Reintentar
                  </button>
                </div>
              ) : filteredCategorias.length === 0 ? (
                <div className="text-center py-12">
                  <div className="w-16 h-16 bg-gray-100 dark:bg-slate-800 rounded-full flex items-center justify-center mx-auto mb-4">
                    <ImageIcon className="w-8 h-8 text-gray-400" />
                  </div>
                  <h4 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                    No se encontraron categorías
                  </h4>
                  <p className="text-sm text-gray-600 dark:text-gray-300 mb-4">
                    {searchTerm 
                      ? `No hay categorías que coincidan con "${searchTerm}"`
                      : 'No hay categorías disponibles. Asegúrate de tener datos en Firestore.'
                    }
                  </p>
                  <button
                    onClick={() => updateOnModalOpen()}
                    className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200"
                  >
                    Actualizar
                  </button>
                </div>
              ) : (
                <div>
                  {viewMode === 'folders' ? renderFoldersView() : renderListView()}
                </div>
              )}
            </div>

            {/* Información de debug */}
            {showDebugInfo && (
              <div className="border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 max-h-80 overflow-y-auto">
                <div className="p-4">
                  <div className="flex items-center justify-between mb-3">
                    <h4 className="text-sm font-semibold text-gray-700 dark:text-gray-300">Debug Info</h4>
                    <button
                      onClick={() => setShowDebugInfo(false)}
                      className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-200"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                  <div className="text-xs text-gray-600 dark:text-gray-400 space-y-2">
                    <div className="bg-white dark:bg-slate-700 p-2 rounded border">
                      <strong>Estado del Usuario:</strong><br/>
                      Usuario: {user?.uid || 'No autenticado'}<br/>
                      Email: {user?.email || 'N/A'}
                    </div>
                    
                    <div className="bg-white dark:bg-slate-700 p-2 rounded border">
                      <strong>Estado de Firebase:</strong><br/>
                      Firebase disponible: {window.auth ? '✅ Sí' : '❌ No'}<br/>
                      Firestore disponible: {window.auth?.db ? '✅ Sí' : '❌ No'}<br/>
                      Storage disponible: {window.auth?.storage ? '✅ Sí' : '❌ No'}
                    </div>
                    
                    <div className="bg-white dark:bg-slate-700 p-2 rounded border">
                      <strong>Datos Cargados:</strong><br/>
                      Categorías cargadas: {categorias.length}<br/>
                      Total productos: {categorias.reduce((sum, cat) => sum + cat.productos.length, 0)}<br/>
                      Estado de carga: {loading ? '🔄 Cargando...' : '✅ Completado'}
                    </div>
                    
                    <div className="bg-white dark:bg-slate-700 p-2 rounded border">
                      <strong>Categorías Detalladas:</strong><br/>
                      {categorias.length === 0 ? (
                        <span className="text-red-500">No hay categorías cargadas</span>
                      ) : (
                        <div className="mt-1 space-y-1">
                          {categorias.map(cat => (
                            <div key={cat.id} className="text-xs">
                              <span className="font-medium">{cat.name}:</span> {cat.productos.length} productos 
                              {cat.isEmpty && <span className="text-orange-500"> (vacía)</span>}
                              {cat.productos.length > 0 && (
                                <div className="ml-2 text-gray-500">
                                  {cat.productos.map(p => p.sku).join(', ')}
                                </div>
                              )}
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                    
                    {error && (
                      <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded border border-red-200 dark:border-red-800">
                        <strong className="text-red-600 dark:text-red-400">Error:</strong><br/>
                        <span className="text-red-600 dark:text-red-400">{error}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Footer con acciones */}
            {selectedProduct && (
              <div className="px-6 py-4 border-t border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-3">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <div>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        Producto seleccionado: {selectedProduct.sku}
                      </p>
                      <p className="text-xs text-gray-600 dark:text-gray-300">
                        {selectedProduct.marca} • {selectedProduct.genero} • ${selectedProduct.precioVenta}
                      </p>
                    </div>
                  </div>
                  <div className="flex space-x-3">
                    {onGenerateWithAI && (
                      <button
                        onClick={() => handleGenerateWithAI(selectedProduct)}
                        className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 text-sm"
                      >
                        <Wand2 className="w-4 h-4 inline mr-2" />
                        Generar con IA
                      </button>
                    )}
                    {onPublish && (
                      <button
                        onClick={() => handlePublish(selectedProduct)}
                        className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors duration-200 text-sm"
                      >
                        Publicar
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default PublicarConIA;
